import { useMemo } from 'react'
import { useApplicationSettings } from 'hooks/usePageConfig'
import ComparisonMode, { ComparisonModeStrings } from 'enums/ComparisonMode'
import {
  generatePivotProductStrategy,
  generatePivotTagStrategy,
  generatePivotThemeStrategy,
  generateStaticStrategy
} from 'utils/insightModal/strategy/widgetSettingComposing'
import { WidgetSettingComposingStrategy } from 'utils/insightModal/strategy/widgetSettingComposing/widgetSettingComposingStrategy'
import {
  pivotProductDecorator,
  pivotTagDecorator,
  pivotThemeDecorator
} from 'utils/insightModal/strategy/widgetSettingComposing/decorator'
import { CrosstabWidgetsType } from '../../types'

const useWidgetSettingComposingStrategy = (
  applicationSettings: any,
  rowComparisonMode: ComparisonModeStrings
): WidgetSettingComposingStrategy =>
  useMemo(() => {
    if (!rowComparisonMode) {
      return generateStaticStrategy()
    }

    switch (rowComparisonMode) {
      case ComparisonMode.THEMES:
        return generatePivotThemeStrategy()

      case ComparisonMode.TAGS:
        return generatePivotTagStrategy(applicationSettings)

      case ComparisonMode.DOC_HIERARCHIES:
        return generatePivotProductStrategy(applicationSettings)

      default:
        return generateStaticStrategy()
    }
  }, [rowComparisonMode, applicationSettings])

const useDecoratedWidgetSettingComposingStrategy = (widgetSetting: CrosstabWidgetsType) => {
  const applicationSettings = useApplicationSettings()
  const { colComparisonMode, rowComparisonMode } = widgetSetting

  const strategy = useWidgetSettingComposingStrategy(applicationSettings, rowComparisonMode!)

  switch (colComparisonMode) {
    case ComparisonMode.THEMES:
      return pivotThemeDecorator(strategy)

    case ComparisonMode.TAGS:
      return pivotTagDecorator(applicationSettings, strategy)

    case ComparisonMode.DOC_HIERARCHIES:
      return pivotProductDecorator(applicationSettings, strategy)

    default:
      throw new Error(`Unsupported comparison mode: ${colComparisonMode}`)
  }
}

export default useDecoratedWidgetSettingComposingStrategy
